import React, { useState } from "react";
import {
  VakLastTestResponse,
  VakTestData,
  VakType,
  VakCategoryInfo,
} from "../../typings/vak";
import RadarChart from "../RadarChart";
import Button from "../UI/Button";
import { Card, CardContent, CardHeader } from "../UI/Card";
import { Eye } from "lucide-react";
import VakPdfDownload from "./VakPdfDownload";
import VakAiRecommendation from "./VakAiRecommendation";

interface VakLastTestProps {
  lastTest: VakLastTestResponse;
  onViewDetail: () => void;
}

const VAK_CATEGORIES: VakCategoryInfo[] = [
  {
    type: "V",
    name: "Visual",
    title: "Visual",
    description: "",
    color: "#FF6384",
  },
  {
    type: "A",
    name: "Auditory",
    title: "Auditory",
    description: "",
    color: "#36A2EB",
  },
  {
    type: "K",
    name: "Kinesthetic",
    title: "Kinesthetic",
    description: "",
    color: "#FFCE56",
  },
];

const VakLastTest: React.FC<VakLastTestProps> = ({
  lastTest,
  onViewDetail,
}) => {
  const [expandedCategories, setExpandedCategories] = useState<{
    [key: string]: boolean;
  }>({});

  const getRadarData = (testData: VakTestData) => {
    return {
      labels: [
        "Visual",
        "Auditory",
        "Kinesthetic",
      ],
      datasets: [
        {
          label: "SLP Score",
          data: [
            testData.v,
            testData.a,
            testData.k,
          ],
          backgroundColor: "rgba(54, 162, 235, 0.2)",
          borderColor: "rgba(54, 162, 235, 1)",
          borderWidth: 2,
          pointBackgroundColor: "rgba(54, 162, 235, 1)",
        },
      ],
    };
  };

  const toggleExpanded = (categoryType: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryType]: !prev[categoryType],
    }));
  };

  const getTop3Categories = () => {
    return VAK_CATEGORIES.map((category) => ({
      ...category,
      score: lastTest.data.test[
        category.type.toLowerCase() as keyof VakTestData
      ] as number,
    }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  };

  return (
    <Card className="mt-4">
      <CardHeader className="border-b border-indigo-50 bg-indigo-50/30">
        <div className="flex justify-between items-center">
          <h5 className="text-lg font-bold text-indigo-900">
            Hasil Terakhir
          </h5>
          <span className="text-xs text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full font-medium">
              {new Date(lastTest.data.test.created_at).toLocaleDateString(
                "id-ID",
                {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }
              )}
            </span>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div>
          <div className="flex flex-col md:flex-row md:flex-wrap">
            <div className="md:w-full mb-3">
              <h6 className="text-center font-semibold mb-3">Grafik</h6>
              <div style={{ height: "250px" }}>
                <RadarChart data={getRadarData(lastTest.data.test)} />
              </div>
            </div>

            <div className="md:w-full">
              <div className="riasec-scores">
                {getTop3Categories().map((category, index) => {
                  const isExpanded = expandedCategories[category.type];

                  return (
                    <div key={category.type} className="mb-3 pb-2 border-b">
                      <div className="flex justify-between items-center mb-1">
                        <span style={{ color: category.color }}>
                          <strong>
                            #{index + 1} {category.title}
                          </strong>
                        </span>
                        <span
                          className="inline-block px-2 py-1 text-xs font-medium rounded"
                          style={{
                            backgroundColor: category.color,
                            color: "#fff",
                            fontWeight: "bold",
                          }}
                        >
                          {category.score}
                        </span>
                      </div>
                      <small className="text-gray-500 block mb-2">
                        {category.description}
                      </small>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex flex-col mt-3">
            <Button
              primary
              className="mb-2 gradient-primary"
              onClick={onViewDetail}
            >
              <Eye /> Lihat Detail Tes
            </Button>
            <VakPdfDownload testId={lastTest.data.test.id} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VakLastTest;